import { UserIdentifierType } from '@fuul/sdk';
import { getStellarExplorerAddressUrl, getStellarExplorerTxUrl, isStellarChainId } from 'src/config/stellar';
import { TriggerAmountSource } from 'src/modules/conversions/domain';

import { NetworkData, NETWORKS } from '../types/network';

const BASE_CURRENCY_LOGOS: Record<string, string> = {
  ETH: '/assets/svg/ethereum.svg',
  BTC: '/assets/svg/bitcoin.svg',
};

const getCurrencyLogoByCurrencyName = (name: string): string | null => {
  return BASE_CURRENCY_LOGOS[name] ?? null;
};

export const formatUrl = (url: string): string => (url.startsWith('http') ? url : 'http://' + url);

const maskEthereumAddress = (address?: string | null): string => {
  if (!address) return '-';
  if (address.length <= 8) return address;

  const prefix = address.slice(0, 6);
  const suffix = address.slice(-4);
  return `${prefix}...${suffix}`;
};

const maskSolanaAddress = (address?: string | null): string => {
  if (!address) return '-';
  if (address.length <= 10) return address;

  const prefix = address.slice(0, 6);
  const suffix = address.slice(-6);
  return `${prefix}...${suffix}`;
};

export const maskAddressValue = (
  address?: string | null,
  userIdentifierType: UserIdentifierType | string = UserIdentifierType.EvmAddress,
): string => {
  if (!address) return '-';

  if (address.length <= 8) return address;

  switch (userIdentifierType) {
    case UserIdentifierType.EvmAddress:
      return maskEthereumAddress(address);
    case UserIdentifierType.SolanaAddress:
      // TODO: Implement solana address masking
      return maskSolanaAddress(address);
    case UserIdentifierType.StellarAddress: // 56-char base32 → 6/6 long masking, not EVM 6/4
    case UserIdentifierType.SuiAddress: // previously fell through to default:throw (latent crash)
    case UserIdentifierType.XRPLAddress: // previously fell through to default:throw (latent crash)
      return maskSolanaAddress(address);
    case UserIdentifierType.Email:
      // Emails are user-readable; do not mask.
      return address;
    default:
      throw new Error(`Unsupported blockchain type: ${userIdentifierType}`);
  }
};

export const getAddressScannerUrl = (
  query: keyof NetworkData,
  queryValue: string | number,
  address: string,
): string | null => {
  if (query === 'chainId' && isStellarChainId(queryValue)) {
    return getStellarExplorerAddressUrl(address, queryValue);
  }
  return getScannerUrl(query, queryValue, address, 'address');
};

export const getTransactionScannerUrl = (
  query: keyof NetworkData,
  queryValue: string | number,
  address: string,
): string | null => {
  if (query === 'chainId' && isStellarChainId(queryValue)) {
    return getStellarExplorerTxUrl(address, queryValue);
  }
  return getScannerUrl(query, queryValue, address, 'tx');
};

const getScannerUrl = (
  query: keyof NetworkData,
  queryValue: string | number,
  address: string,
  path: string,
): string | null => {
  const networkData = NETWORKS.find((n) => n[query] === queryValue);

  return networkData ? `${networkData?.scannerBaseUrl}/${path}/${address}` : null;
};

export const addTwitterHandlePrefix = (value: string): string => {
  if (!value.startsWith('@')) {
    return `@${value}`;
  }
  return value;
};

export const calculateMaxDecimals = (amount: number): number => {
  const decimalStr = amount.toString().split('.')[1];

  if (!decimalStr) {
    return 0;
  }

  return decimalStr.length;
};

export const formatDecimalAmount = (amount: number): string => {
  const maxDecimals = calculateMaxDecimals(amount);

  return new Intl.NumberFormat('default', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).format(amount);
};

export const formatPercentage = (amount: number): string => {
  const maxDecimals = calculateMaxDecimals(amount);

  return new Intl.NumberFormat('default', {
    style: 'percent',
    minimumFractionDigits: maxDecimals < 4 ? 0 : 4,
    maximumFractionDigits: maxDecimals,
  }).format(amount / 100);
};

export const getNumberWithOrdinal = (n: number): string => {
  let numberWithOrdinal = `${n}th`;

  if (n % 10 == 1 && n % 100 != 11) {
    numberWithOrdinal = `${n}st`;
  } else if (n % 10 == 2 && n % 100 != 12) {
    numberWithOrdinal = `${n}nd`;
  } else if (n % 10 == 3 && n % 100 != 13) {
    numberWithOrdinal = `${n}rd`;
  }

  return numberWithOrdinal;
};

export const formatToCompactNumber = (n: number): string => {
  const usformatter = Intl.NumberFormat('en-US', {
    compactDisplay: 'short',
    notation: 'compact',
  });
  return usformatter.format(n);
};

export const formatToCompactCurrency = (n: number, currency = 'USD'): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    compactDisplay: 'short',
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return formatter.format(n);
};

export const formatNumber = (
  amount: number,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  },
): string => {
  const { minimumFractionDigits = 0, maximumFractionDigits = 3 } = options || {};
  const num = Number(amount) || 0;
  if (num === 0) return '0';

  const fixed = num.toFixed(maximumFractionDigits);
  // Remove trailing zeros after the decimal point and the point if it's an integer
  const formatted = parseFloat(fixed).toString();

  const [_, decimalPart] = formatted.split('.');

  if (decimalPart && decimalPart.length < minimumFractionDigits) {
    return num.toFixed(minimumFractionDigits);
  }

  return formatted;
};

export const getDecimalCount = (value: number): number => {
  const decimalCount = value.toString().split('.')[1];
  return decimalCount ? decimalCount.length : 0;
};

const isDecimal = (amount: number): boolean => amount % 1 !== 0;

export const getFormattedAmountPerBaseCurrency = (
  amount: number,
  currencySymbol: string,
  baseCurrencySymbol?: string,
  isPointsReward?: boolean,
  amountSource?: TriggerAmountSource | null,
): string => {
  const isUnit = baseCurrencySymbol && baseCurrencySymbol.toLowerCase() !== 'none';

  if (isDecimal(amount)) {
    const decimalPlaces = amount.toString().split('.')[1].length;
    const multiplier = Math.pow(10, decimalPlaces);

    const adjustedAmount = formatDecimalAmount(amount * multiplier);
    const pointsLabel = adjustedAmount === '1' ? 'POINT' : 'POINTS';

    return isPointsReward
      ? `${adjustedAmount} ${pointsLabel} per ${formatDecimalAmount(multiplier)} ${baseCurrencySymbol}`
      : isUnit
        ? `${adjustedAmount} ${currencySymbol} per ${formatDecimalAmount(multiplier)} ${baseCurrencySymbol}`
        : `${formatPercentage(Number(amount) * 100)} paid in ${currencySymbol}`;
  } else {
    const pointsLabel = Number(amount) === 1 ? 'POINT' : 'POINTS';

    return isPointsReward
      ? `${formatDecimalAmount(amount)} ${pointsLabel} per ${baseCurrencySymbol}`
      : isUnit
        ? `${formatDecimalAmount(amount)} ${currencySymbol} per ${baseCurrencySymbol}`
        : `${formatPercentage(Number(amount) * 100)} of ${amountSource} paid in ${baseCurrencySymbol}`;
  }
};

interface FormattedAmountPerBaseCurrency {
  adjustedAmount: string;
  adjustedMultiplier: string | null;
  currencyImageSrc: string | null;
  baseCurrencyImageSrc: string | null;
  pointsLabel: string;
  action: string;
  isSameCurrency: boolean;
}

export const getFormattedAmountPerBaseCurrencyObject = (
  amount: number,
  currencySymbol: string,
  baseCurrencySymbol?: string,
  amountSource?: TriggerAmountSource,
  isPoints?: boolean,
): FormattedAmountPerBaseCurrency => {
  const isDecimalAmount = isDecimal(amount);
  const isSameCurrency = baseCurrencySymbol == currencySymbol;

  let amountStr: string;
  let decimalPlaces: number;

  const amountToString = amount.toString();

  if (amountToString.includes('e') || amountToString.includes('E')) {
    const fixedStr = amount.toFixed(20);
    amountStr = fixedStr.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
    decimalPlaces = amountStr.includes('.') ? amountStr.split('.')[1].length : 0;
  } else {
    amountStr = amountToString;
    decimalPlaces = isDecimalAmount && amountStr.includes('.') ? amountStr.split('.')[1].length : 0;
  }

  const multiplier = Math.pow(10, decimalPlaces);

  const adjustedAmount = isSameCurrency
    ? formatPercentage(Number(amount) * 100)
    : isDecimalAmount && !isPoints
      ? formatDecimalAmount(amount * multiplier)
      : formatDecimalAmount(amount);
  const adjustedMultiplier: string | null =
    isDecimalAmount && !isSameCurrency && !isPoints ? formatDecimalAmount(multiplier) : null;
  const pointsLabel = amount === 1 ? 'POINT' : 'POINTS';
  const currencyImageSrc: string | null = isSameCurrency
    ? null
    : isPoints
      ? '/assets/svg/coin.svg'
      : getCurrencyLogoByCurrencyName(currencySymbol);
  const baseCurrencyImageSrc: string | null =
    isPoints && isSameCurrency
      ? '/assets/svg/coin.svg'
      : baseCurrencySymbol
        ? getCurrencyLogoByCurrencyName(baseCurrencySymbol)
        : null;
  const action = isSameCurrency ? `of ${amountSource} paid in` : 'per';

  return {
    adjustedAmount,
    adjustedMultiplier,
    currencyImageSrc,
    baseCurrencyImageSrc,
    pointsLabel,
    action,
    isSameCurrency,
  };
};

export const processNumbers = (numbers: number[], operation: 'add', decimalPlaces = 2): number => {
  let result;

  if (operation === 'add') {
    result = numbers.reduce((acc, num) => acc + num, 0);
  } else {
    throw new Error('Unsupported operation');
  }

  return Number(result.toFixed(decimalPlaces));
};

/**
 * Format share percentage with up to 3 decimal places.
 * If less than 0.001, show ">0.001" and return the full value for tooltip.
 */
export const formatSharePercentage = (share: number): { display: string; tooltip: string } => {
  const MIN_THRESHOLD = 0.01;

  if (share < MIN_THRESHOLD && share > 0) {
    return {
      display: '>0.01%',
      tooltip: `${share.toFixed(2)}%`,
    };
  }

  // Format with up to 3 decimal places, removing trailing zeros
  const formatted = new Intl.NumberFormat('default', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(share);

  return {
    display: `${formatted}%`,
    tooltip: `${formatted}%`,
  };
};

const thousandsSeparatorFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 18,
});

/**
 * Format a committed numeric value with thousands separators (e.g., 500000 -> "500,000").
 * Use for display when the input is NOT focused. Lossy for in-progress input (drops trailing dots/zeros).
 */
export const formatWithThousandsSeparator = (value: number | string | undefined): string => {
  if (value === undefined || value === '') return '';

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) return '';

  return thousandsSeparatorFormatter.format(numValue);
};

/**
 * Format a raw numeric string with thousands separators while preserving trailing dots and zeros
 * (e.g., "500000." -> "500,000.", "1500.50" -> "1,500.50"). Use for display while the user is typing.
 */
export const formatLiveInput = (raw: string): string => {
  if (!raw) return '';
  const [intPart, ...rest] = raw.split('.');
  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return rest.length > 0 ? `${formatted}.${rest.join('')}` : formatted;
};
